import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';

import { Box } from './Box';
import { Icon } from './Icon';

export interface TitleAndCloseProps {
  title?: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  titleAlignment?: 'left' | 'center';
  leftAdornment?: React.ReactNode;
  rightAdornment?: React.ReactNode;
  onClose?: () => void;
  noWrap?: boolean;
  titleVariant?: 'h5' | 'h6';
}

export const TitleAndClose: React.FC<TitleAndCloseProps> = ({
  title,
  subtitle,
  titleAlignment = 'left',
  onClose,
  leftAdornment,
  rightAdornment,
  titleVariant = 'h5',
  noWrap,
}) => {
  const hasRightContent = rightAdornment || onClose;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        px: 2,
        pb: 2,
        maxWidth: '100%',
      }}
    >
      {/* Left section - placeholder for centered layout balance */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          pt: 2,
          minWidth: titleAlignment === 'center' && hasRightContent ? 40 : 0,
          flex: titleAlignment === 'center' ? '0 0 auto' : undefined,
        }}
      >
        {leftAdornment}
      </Box>

      {/* Center/Main section - title */}
      <Box
        sx={{
          flex: 1,
          maxWidth:
            titleAlignment === 'center'
              ? '100%'
              : `calc(100% - ${hasRightContent ? 50 : 0}px)`,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            minHeight: '52px',
            display: 'flex',
            flexDirection: 'column',
            alignItems:
              titleAlignment === 'center' ? 'center' : 'flex-start',
            justifyContent: 'center',
            pt: 2,
            width: '100%',
          }}
        >
          <Typography
            noWrap={noWrap}
            variant={titleVariant}
            sx={[
              {
                width: '100%',
                py: 0,
                textAlign: titleAlignment,
              },
              !noWrap && {
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
              },
            ]}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, ml: titleAlignment === 'center' ? 0 : '1px' }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 2,
          minWidth: hasRightContent ? 40 : 0,
          justifyContent: 'flex-end',
        }}
      >
        {rightAdornment}
        {onClose && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.vars.palette.neutral[500],
              ml: rightAdornment ? 1 : 0,
            }}
          >
            <Icon
              color="action"
              icon={faTimes}
            />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
